import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { authenticatedFetch } from '../../utils/api';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import { Checkbox } from '../ui/checkbox';
import { 
  Users, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  UserCheck, 
  UserX,
  Calendar,
  School,
  Loader2,
  Shield,
  Clock,
  Mail,
  User,
  CreditCard,
  ToggleLeft,
  ToggleRight,
  CheckCircle,
  XCircle,
  LogIn,
  GraduationCap
} from 'lucide-react';

// Password reset validation schema
const passwordResetSchema = z.object({
  new_password: z.string().min(8, "Password must be at least 8 characters")
});

// Class and subject definitions (same as TeacherDashboard)
const kgPrimaryClasses = [
  'KG 1', 'KG 2', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6'
];

const jhsSubjects = [
  'Mathematics', 'English Language', 'Integrated Science', 'Social Studies', 'Religious and Moral Education', 
  'Computing', 'Creative Arts', 'Ghanaian Language', 'French', 'Career Technology'
];

// User Manager - Fresh Implementation to avoid caching issues
const UserManagerFresh = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);
  const [selectedPromoRankId, setSelectedPromoRankId] = useState('');
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [selectedClassesSubjects, setSelectedClassesSubjects] = useState([]);
  const [activeTermName, setActiveTermName] = useState('Term');
  const [isLoggingInAsUser, setIsLoggingInAsUser] = useState(null); // stores user id being logged in as
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(passwordResetSchema)
  });

  // Load active term name
  const loadActiveTerm = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/admin/terms`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const terms = await response.json();
        const activeTerm = terms.find(t => t.is_active);
        if (activeTerm) {
          setActiveTermName(activeTerm.name);
        }
      }
    } catch (error) {
      console.error('Error loading active term:', error);
    }
  };

  // Load users
  const loadUsers = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/admin/users`, {
        credentials: 'include'
      });

      if (response.ok) {
        const userData = await response.json();
        setUsers(userData);
        setFilteredUsers(userData);
      } else {
        throw new Error('Failed to load users');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search and filters
  useEffect(() => {
    let filtered = users.filter(user => {
      const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      
      const matchesSector = sectorFilter === 'all' || (user.sector || 'public') === sectorFilter;
      
      const matchesSubscription = subscriptionFilter === 'all' || 
                                 user.subscription_status === subscriptionFilter;
      
      return matchesSearch && matchesRole && matchesSector && matchesSubscription;
    });
    
    setFilteredUsers(filtered);
  }, [users, searchTerm, roleFilter, sectorFilter, subscriptionFilter]);

  // Reset user password
  const onResetPassword = async (data) => {
    if (!selectedUser) return;
    
    setIsUpdatingPassword(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/admin/users/${selectedUser.id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: `Password reset for ${selectedUser.name}`,
          variant: "default"
        });
        setIsPasswordDialogOpen(false);
        reset();
        setSelectedUser(null);
      } else {
        throw new Error('Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast({
        title: "Error",
        description: "Failed to reset password",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Delete user
  const deleteUser = async (user) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/admin/users/${user.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: `User ${user.name} deleted successfully`,
          variant: "default"
        });
        // Reload users
        loadUsers();
      } else {
        throw new Error('Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: "Error",
        description: "Failed to delete user",
        variant: "destructive"
      });
    }
  };

  // Login as user (admin impersonation)
  const loginAsUser = async (user) => {
    // Prevent impersonating admins
    if (user.role === 'admin' || user.role === 'sub_admin') {
      toast({
        title: "Not Allowed",
        description: "Cannot login as admin or sub-admin users for security reasons",
        variant: "destructive"
      });
      return;
    }

    setIsLoggingInAsUser(user.id);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Store original admin token BEFORE making the request
      const currentAdminToken = localStorage.getItem('auth_token');
      if (currentAdminToken) {
        localStorage.setItem('admin_original_token', currentAdminToken);
      }
      
      const response = await authenticatedFetch(`${backendUrl}/api/admin/login-as-user/${user.id}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store the new user's token
        localStorage.setItem('auth_token', data.token);
        
        // Also store user data for immediate access on page load
        localStorage.setItem('impersonated_user', JSON.stringify(data.user));
        
        toast({
          title: "Success",
          description: `Logged in as ${user.name}. Redirecting...`,
          variant: "default"
        });
        
        // Use full page reload to ensure clean auth state
        // Route to the correct dashboard based on user's role
        const targetRole = data.user?.role;
        let targetUrl = '/dashboard';
        if (targetRole === 'siso') targetUrl = '/siso-dashboard';
        else if (targetRole === 'director' || targetRole === 'dd_supervision' || targetRole === 'secretary' || targetRole === 'hrm' || targetRole === 'personnel' || targetRole === 'supervision_team') targetUrl = '/director-dashboard';
        else if (targetRole === 'other_ges_staff') targetUrl = '/ges-staff-dashboard';
        else if (targetRole === 'admin' || targetRole === 'sub-admin') targetUrl = '/admin';
        
        setTimeout(() => {
          window.location.href = targetUrl;
        }, 500);
        
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to login as user');
      }
    } catch (error) {
      console.error('Error logging in as user:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to login as user",
        variant: "destructive"
      });
    } finally {
      setIsLoggingInAsUser(null);
    }
  };

  // Fetch subscription details for a user
  const fetchSubscriptionDetails = async (user) => {
    setSelectedUser(user);
    setIsSubscriptionDialogOpen(true);
    setSubscriptionDetails(null);
    setSelectedClassesSubjects([]); // Reset selection when opening dialog
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await authenticatedFetch(`${backendUrl}/api/admin/users/${user.id}/subscription`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionDetails(data);
        // Pre-select classes/subjects from active subscription if exists
        if (data.active_subscription?.classes_subjects) {
          setSelectedClassesSubjects(data.active_subscription.classes_subjects);
        }
      } else {
        throw new Error('Failed to fetch subscription details');
      }
    } catch (error) {
      console.error('Error fetching subscription details:', error);
      toast({
        title: "Error",
        description: "Failed to fetch subscription details",
        variant: "destructive"
      });
    }
  };

  // Handle class/subject selection
  const handleSelectionChange = (item, checked) => {
    if (checked) {
      setSelectedClassesSubjects(prev => [...prev, item]);
    } else {
      setSelectedClassesSubjects(prev => prev.filter(i => i !== item));
    }
  };

  // Manage subscription (activate/deactivate)
  const manageSubscription = async (action) => {
    if (!selectedUser) return;
    
    // Validate selection for activation
    if (action === 'activate' && selectedClassesSubjects.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one class or subject",
        variant: "destructive"
      });
      return;
    }
    
    setIsManagingSubscription(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const requestBody = { 
        action,
        classes_subjects: action === 'activate' ? selectedClassesSubjects : []
      };
      
      const response = await authenticatedFetch(`${backendUrl}/api/admin/users/${selectedUser.id}/subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: "Success",
          description: data.message,
          variant: "default"
        });
        
        // Refresh subscription details
        await fetchSubscriptionDetails(selectedUser);
        
        // Reload users list
        loadUsers();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to manage subscription');
      }
    } catch (error) {
      console.error('Error managing subscription:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to manage subscription",
        variant: "destructive"
      });
    } finally {
      setIsManagingSubscription(false);
    }
  };

  // Manage promotion exam subscription
  const managePromoSubscription = async (action, rankId) => {
    if (!selectedUser) return;
    setIsManagingSubscription(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const endpoint = action === 'activate'
        ? `${backendUrl}/api/admin/promotion/subscription/activate`
        : `${backendUrl}/api/admin/promotion/subscription/deactivate`;

      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: selectedUser.id, rank_id: rankId })
      });

      if (response.ok) {
        const data = await response.json();
        toast({ title: "Success", description: data.message });
        await fetchSubscriptionDetails(selectedUser);
      } else {
        const err = await response.json();
        throw new Error(err.detail || 'Failed');
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsManagingSubscription(false);
      setSelectedPromoRankId('');
    }
  };

  // Get role badge variant
  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin':
        return 'default';
      case 'sub-admin':
        return 'secondary';
      case 'headteacher':
        return 'outline';
      case 'teacher':
        return 'outline';
      default:
        return 'outline';
    }
  };

  // Get subscription status badge variant
  const getSubscriptionBadgeVariant = (status) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'inactive':
        return 'secondary';
      case 'expired':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  useEffect(() => {
    loadUsers();
    loadActiveTerm();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Loading users...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            User Management - FRESH VERSION 🚀
          </CardTitle>
          <CardDescription>
            View user profiles, manage subscriptions, reset passwords, and delete accounts
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <select
                value={sectorFilter}
                onChange={(e) => setSectorFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Sectors</option>
                <option value="public">Public Sector</option>
                <option value="private">Private Sector</option>
              </select>
              
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Roles</option>
                <option value="teacher">Teachers</option>
                <option value="headteacher">Headteachers</option>
                <option value="siso">SISOs</option>
                <option value="director">Directors</option>
                <option value="dd_supervision">DD Supervision</option>
                <option value="secretary">Secretaries</option>
                <option value="other_ges_staff">Other GES Staff</option>
              </select>
              
              <select
                value={subscriptionFilter}
                onChange={(e) => setSubscriptionFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Subscriptions</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>

          {/* Stats Dashboard */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <Users className="h-4 w-4 text-blue-500 mr-2" />
                  <div>
                    <p className="text-sm font-medium">Total Users</p>
                    <p className="text-2xl font-bold">{users.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <UserCheck className="h-4 w-4 text-green-500 mr-2" />
                  <div>
                    <p className="text-sm font-medium">Active Subscriptions</p>
                    <p className="text-2xl font-bold">
                      {users.filter(u => u.subscription_status === 'active').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <School className="h-4 w-4 text-purple-500 mr-2" />
                  <div>
                    <p className="text-sm font-medium">Teachers</p>
                    <p className="text-2xl font-bold">
                      {users.filter(u => u.role === 'teacher').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <Shield className="h-4 w-4 text-orange-500 mr-2" />
                  <div>
                    <p className="text-sm font-medium">Headteachers</p>
                    <p className="text-2xl font-bold">
                      {users.filter(u => u.role === 'headteacher').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Users Table */}
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[200px]">User Profile</TableHead>
                  <TableHead className="min-w-[100px]">Role</TableHead>
                  <TableHead className="min-w-[140px]">Subscription Status</TableHead>
                  <TableHead className="min-w-[100px]">{activeTermName}</TableHead>
                  <TableHead className="min-w-[100px]">Created</TableHead>
                  <TableHead className="min-w-[100px]">Last Login</TableHead>
                  <TableHead className="min-w-[180px] text-right pr-6">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getRoleBadgeVariant(user.role)}>
                          {user.role === 'headteacher' ? 'Head Teacher' : 
                           user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.subscription_status ? (
                          <Badge variant={getSubscriptionBadgeVariant(user.subscription_status)}>
                            {user.subscription_status.charAt(0).toUpperCase() + user.subscription_status.slice(1)}
                          </Badge>
                        ) : (
                          <span className="text-gray-400">No subscription</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.subscription_term || (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell>{formatDate(user.last_login)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end pr-4">
                          
                          {/* View Profile */}
                          <Dialog 
                            open={isProfileDialogOpen && selectedUser?.id === user.id} 
                            onOpenChange={(open) => {
                              setIsProfileDialogOpen(open);
                              if (!open) setSelectedUser(null);
                            }}
                          >
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedUser(user)}
                              >
                                <Eye className="h-3 w-3" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md">
                              <DialogHeader>
                                <DialogTitle>User Profile</DialogTitle>
                                <DialogDescription>
                                  Detailed information for {user.name}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <User className="h-4 w-4 text-gray-500" />
                                      <span className="text-sm font-medium">Name</span>
                                    </div>
                                    <p className="text-sm">{user.name}</p>
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <Mail className="h-4 w-4 text-gray-500" />
                                      <span className="text-sm font-medium">Email</span>
                                    </div>
                                    <p className="text-sm">{user.email}</p>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <Shield className="h-4 w-4 text-gray-500" />
                                      <span className="text-sm font-medium">Role</span>
                                    </div>
                                    <Badge variant={getRoleBadgeVariant(user.role)}>
                                      {user.role === 'headteacher' ? 'Head Teacher' : 
                                       user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                    </Badge>
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <UserCheck className="h-4 w-4 text-gray-500" />
                                      <span className="text-sm font-medium">Subscription</span>
                                    </div>
                                    {user.subscription_status ? (
                                      <Badge variant={getSubscriptionBadgeVariant(user.subscription_status)}>
                                        {user.subscription_status.charAt(0).toUpperCase() + user.subscription_status.slice(1)}
                                      </Badge>
                                    ) : (
                                      <span className="text-gray-400 text-sm">No subscription</span>
                                    )}
                                  </div>
                                </div>

                                <div>
                                  <div className="flex items-center gap-2 mb-2">
                                    <School className="h-4 w-4 text-gray-500" />
                                    <span className="text-sm font-medium">Subscription Term</span>
                                  </div>
                                  <p className="text-sm">{user.subscription_term || 'Not assigned'}</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <Calendar className="h-4 w-4 text-gray-500" />
                                      <span className="text-sm font-medium">Created</span>
                                    </div>
                                    <p className="text-sm">{formatDateTime(user.created_at)}</p>
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <Clock className="h-4 w-4 text-gray-500" />
                                      <span className="text-sm font-medium">Last Login</span>
                                    </div>
                                    <p className="text-sm">{formatDateTime(user.last_login)}</p>
                                  </div>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                          {/* Reset Password */}
                          <Dialog 
                            open={isPasswordDialogOpen && selectedUser?.id === user.id} 
                            onOpenChange={(open) => {
                              setIsPasswordDialogOpen(open);
                              if (!open) {
                                setSelectedUser(null);
                                reset();
                              }
                            }}
                          >
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedUser(user)}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Reset Password</DialogTitle>
                                <DialogDescription>
                                  Reset password for {user.name}
                                </DialogDescription>
                              </DialogHeader>
                              <form onSubmit={handleSubmit(onResetPassword)}>
                                <div className="space-y-4">
                                  <div>
                                    <label className="block text-sm font-medium mb-2">
                                      New Password
                                    </label>
                                    <Input
                                      type="password"
                                      {...register('new_password')}
                                      placeholder="Enter new password"
                                    />
                                    {errors.new_password && (
                                      <p className="text-sm text-red-500 mt-1">
                                        {errors.new_password.message}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <DialogFooter className="mt-6">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsPasswordDialogOpen(false)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button 
                                    type="submit" 
                                    disabled={isUpdatingPassword}
                                  >
                                    {isUpdatingPassword ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Resetting...
                                      </>
                                    ) : (
                                      'Reset Password'
                                    )}
                                  </Button>
                                </DialogFooter>
                              </form>
                            </DialogContent>
                          </Dialog>

                          {/* Manage Subscription */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fetchSubscriptionDetails(user)}
                            title="Manage Subscription"
                          >
                            <CreditCard className="h-3 w-3 text-blue-500" />
                          </Button>

                          {/* Login as User */}
                          {user.role !== 'admin' && user.role !== 'sub_admin' && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  title="Login as this user"
                                  disabled={isLoggingInAsUser === user.id}
                                >
                                  {isLoggingInAsUser === user.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin text-purple-500" />
                                  ) : (
                                    <LogIn className="h-3 w-3 text-purple-500" />
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Login as User</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    You are about to login as <strong>{user.name}</strong> ({user.email}).
                                    <br /><br />
                                    This will log you out of your admin account and redirect you to the user&apos;s dashboard.
                                    You can return to admin by logging out and logging back in with your admin credentials.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => loginAsUser(user)}
                                    className="bg-purple-600 hover:bg-purple-700"
                                  >
                                    <LogIn className="h-4 w-4 mr-2" />
                                    Login as {user.name}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}

                          {/* Delete User */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Trash2 className="h-3 w-3 text-red-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete User</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete {user.name}? This action cannot be undone.
                                  This will also delete all related subscriptions, lesson notes, and folders.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteUser(user)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Results Summary */}
          {filteredUsers.length > 0 && (
            <div className="mt-4 text-sm text-gray-500">
              Showing {filteredUsers.length} of {users.length} users
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscription Management Dialog */}
      <Dialog 
        open={isSubscriptionDialogOpen} 
        onOpenChange={(open) => {
          setIsSubscriptionDialogOpen(open);
          if (!open) {
            setSelectedUser(null);
            setSubscriptionDetails(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Manage Subscription
            </DialogTitle>
            <DialogDescription>
              {selectedUser && `Manage subscription for ${selectedUser.name}`}
            </DialogDescription>
          </DialogHeader>
          
          {!subscriptionDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Loading subscription details...
            </div>
          ) : (
            <div className="space-y-4">
              {/* User Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">User Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>
                    <span className="ml-2 font-medium">{subscriptionDetails.user?.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <span className="ml-2">{subscriptionDetails.user?.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Role:</span>
                    <Badge variant="outline" className="ml-2">{subscriptionDetails.user?.role}</Badge>
                  </div>
                </div>
              </div>

              {/* Current Subscription Status */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Current Subscription</h4>
                {subscriptionDetails.active_subscription ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="font-medium text-green-700">Active Subscription</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>Created: {formatDate(subscriptionDetails.active_subscription.created_at)}</p>
                      {subscriptionDetails.active_subscription.classes_subjects?.length > 0 && (
                        <div>
                          <p className="font-medium mt-2">Subscribed Classes/Subjects:</p>
                          <p className="text-purple-600">{subscriptionDetails.active_subscription.classes_subjects.join(', ')}</p>
                        </div>
                      )}
                      <p className="mt-1">Payment: {subscriptionDetails.active_subscription.payment_status || (subscriptionDetails.active_subscription.paystack_reference ? 'Paid' : 'Admin Granted')}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-500">No active subscription</span>
                  </div>
                )}
              </div>

              {/* Class/Subject Selection - Only show when activating */}
              {!subscriptionDetails.active_subscription && (
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium text-lg">Select Classes/Subjects for Subscription</h4>
                  
                  {/* KG & Primary Classes */}
                  <div>
                    <h5 className="font-medium text-sm mb-2 text-gray-700">KG & Primary Classes</h5>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {kgPrimaryClasses.map((className) => (
                        <div key={className} className="flex items-center space-x-2">
                          <Checkbox
                            id={`class-${className}`}
                            checked={selectedClassesSubjects.includes(className)}
                            onCheckedChange={(checked) => handleSelectionChange(className, checked)}
                          />
                          <label htmlFor={`class-${className}`} className="text-sm cursor-pointer">
                            {className}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h5 className="font-medium text-sm mb-2 text-gray-700">JHS Subjects</h5>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {jhsSubjects.map((subject) => (
                        <div key={subject} className="flex items-center space-x-2">
                          <Checkbox
                            id={`subject-${subject}`}
                            checked={selectedClassesSubjects.includes(subject)}
                            onCheckedChange={(checked) => handleSelectionChange(subject, checked)}
                          />
                          <label htmlFor={`subject-${subject}`} className="text-sm cursor-pointer">
                            {subject}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Selection Summary */}
                  {selectedClassesSubjects.length > 0 && (
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="font-medium text-purple-800">Selected ({selectedClassesSubjects.length}):</p>
                      <p className="text-purple-600 text-sm">{selectedClassesSubjects.join(', ')}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Subscription History */}
              {subscriptionDetails.subscription_history?.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Subscription History ({subscriptionDetails.subscription_history.length})</h4>
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {subscriptionDetails.subscription_history.slice(0, 5).map((sub, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span>{formatDate(sub.created_at)}</span>
                        <Badge variant={sub.status === 'active' ? 'default' : 'secondary'}>
                          {sub.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Promotion Exams Subscription */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-amber-600" />
                  Promotion Exams Subscription
                </h4>
                
                {/* Active promotion subscriptions */}
                {subscriptionDetails.active_promotion_subscriptions?.length > 0 ? (
                  <div className="space-y-2 mb-3">
                    {subscriptionDetails.active_promotion_subscriptions.map((ps) => (
                      <div key={ps.id} className="flex items-center justify-between bg-white p-2 rounded border">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-sm font-medium">{ps.rank_name}</span>
                          <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                          <span className="text-xs text-gray-400">{ps.payment_status === 'admin_activated' ? 'Admin Granted' : 'Paid'}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500 border-red-200 hover:bg-red-50 h-7 text-xs"
                          onClick={() => managePromoSubscription('deactivate', ps.rank_id)}
                          disabled={isManagingSubscription}
                        >
                          Deactivate
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-3">No active promotion subscriptions</p>
                )}

                {/* Add new promotion subscription */}
                {subscriptionDetails.promotion_ranks?.length > 0 && (
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Subscribe to rank:</label>
                      <select
                        value={selectedPromoRankId}
                        onChange={(e) => setSelectedPromoRankId(e.target.value)}
                        className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">Select a rank...</option>
                        {subscriptionDetails.promotion_ranks
                          .filter(r => !subscriptionDetails.active_promotion_subscriptions?.some(s => s.rank_id === r.id))
                          .map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))
                        }
                      </select>
                    </div>
                    <Button
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 h-9"
                      onClick={() => managePromoSubscription('activate', selectedPromoRankId)}
                      disabled={isManagingSubscription || !selectedPromoRankId}
                    >
                      {isManagingSubscription ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Activate'}
                    </Button>
                  </div>
                )}

                {/* Promotion subscription history */}
                {subscriptionDetails.promotion_subscriptions?.length > 0 && (
                  <div className="mt-3 border-t pt-2">
                    <p className="text-xs font-medium text-gray-500 mb-1">History ({subscriptionDetails.promotion_subscriptions.length})</p>
                    <div className="max-h-24 overflow-y-auto space-y-1">
                      {subscriptionDetails.promotion_subscriptions.slice(0, 5).map((sub, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span>{sub.rank_name}</span>
                          <Badge variant={sub.status === 'active' ? 'default' : 'secondary'} className="text-xs">{sub.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                {subscriptionDetails.active_subscription ? (
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => manageSubscription('deactivate')}
                    disabled={isManagingSubscription}
                  >
                    {isManagingSubscription ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Deactivating...
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="h-4 w-4 mr-2" />
                        Deactivate Subscription
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                    onClick={() => manageSubscription('activate')}
                    disabled={isManagingSubscription || selectedClassesSubjects.length === 0}
                  >
                    {isManagingSubscription ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Activating...
                      </>
                    ) : (
                      <>
                        <ToggleRight className="h-4 w-4 mr-2" />
                        Activate Subscription
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsSubscriptionDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagerFresh;